import React, { useEffect, useState } from "react";
import {
  Button,
  Collapse,
  FloatButton,
  Input,
  message,
  Modal,
  Popover,
  Select,
  Table,
} from "antd";
import { useNavigate } from "react-router-dom";
import { getStatusColor } from "../../../util/statusColor";
import {
  useOrganizationsList,
  useUpdateOrganizationStatus,
} from "../../../api/organization";
import TableFilter from "../../TableFilter";
import { MoreOutlined, PlusOutlined } from "@ant-design/icons";

type organizationTableProps = {
  searchText: string;
};

type merchant = {
  id: number;
  name: string;
  type: string;
  status: string;
  phoneNo: string;
  address: string;
};

const _columns = [
  {
    title: "Name",
    dataIndex: "name",
  },
  {
    title: "Type",
    dataIndex: "type",
    render: (type: string) => (type === "MICRO-FINANCE" ? "BANK" : type),
  },
  {
    title: "Email",
    dataIndex: "email",
  },
  {
    title: "Phone",
    dataIndex: "phoneNo",
  },
  {
    title: "Status",
    dataIndex: "status",
    render: (text: string, record: any) => {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            width: 100,
            paddingTop: 6,
            paddingBottom: 6,
            borderRadius: 8.5,
            color: getStatusColor(text),
            backgroundColor: `${getStatusColor(text)}44`,
          }}
        >
          {text}
        </div>
      );
    },
  },
];

const OrganizationTable: React.FC<organizationTableProps> = ({
  searchText,
}) => {
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<
    "ACTIVE" | "INACTIVE" | "PENDING" | "REJECTED" | undefined
  >();
  const [filters, setFilters] = useState<{ label: string; count: number }[]>(
    [],
  );

  const [search, setSearch] = useState<string>("");

  const [selectedFilterIndex, setSelectedFilterIndex] = useState(0);
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState("");
  const [status, setStatus] = useState();
  const [open, setOpen] = useState(false);
  const [merchants, setMerchants] = useState<merchant[]>([]);
  const { data: organizations, isLoading, refetch } = useOrganizationsList();
  const { mutateAsync: updateStatus, isPending } =
    useUpdateOrganizationStatus();

  const onUpdate = async () => {
    try {
      if (selectedId && status) {
        const result = await updateStatus({ id: selectedId, status });
        message.success("Organization status updated successfully.");
      }
    } catch (err: any) {
      message.error("Operation failed");
    } finally {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (search) {
      const _allData = organizations?.data?.data?.map((item: any) => {
        return {
          id: item.id,
          name: item.name,
          email: item.email,
          phoneNo: item.phoneNo,
          type: item.type,
        };
      });

      const _data = _allData.filter(
        (item: any) =>
          item.name?.toLowerCase().includes(search.toLowerCase()) ||
          item.email?.toLowerCase().includes(search.toLowerCase()) ||
          item.phoneNo?.toLowerCase().includes(search.toLowerCase()) ||
          item.type?.toLowerCase().includes(search.toLowerCase()),
      );
      setTableData(_data);
    } else {
      if (
        organizations &&
        organizations.data &&
        organizations.data.data &&
        organizations.data.data.content
      ) {
        setTableData(organizations.data.data.content);
      }
    }
  }, [search, organizations]);

  useEffect(() => {
    if (searchText) {
      // const _allData = merchants.map((item: any) => {
      //     return { id: item.id, name: item.name, type: item.type, email: item.email, status: item.status, phoneNo: item.phoneNo, address: item.address, noDocument: item.noDocument }
      // });

      const _data = merchants.filter(
        (item: any) =>
          item.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.type?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.email?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.status?.toLowerCase().includes(searchText.toLowerCase()) ||
          item.phoneNo?.toLowerCase().includes(searchText.toLowerCase()),
      );
      setTableData(_data);
    } else {
      if (merchants) {
        setTableData(merchants);
      }
    }
  }, [searchText, merchants]);

  useEffect(() => {
    if (organizations && organizations.data && organizations.data.data) {
      const _merchants: merchant[] = organizations.data.data.map(
        (item: any) => {
          return {
            id: item.id,
            name: item.name,
            type: item.type,
            email: item.email,
            status: item.status,
            phoneNo: item.phoneNo,
            address: item.address,
            noDocument: item.noDocument,
          };
        },
      );

      if (_merchants) {
        setMerchants(_merchants);
      }
    }
  }, [organizations]);

  const columns = [
    ..._columns,
    {
      title: "Actions",
      render: (text: string, record: any, index: number) => (
        <div style={{ display: "flex", gap: 12 }}>
          <Button
            type="primary"
            disabled={record.noDocument === 0}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedId(record.id);
              setStatus(record.status);
              setOpen(true);
            }}
          >
            Update Status
          </Button>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="hidden md:flex flex-col gap-2">
        <Modal
          title={<div style={{ color: "black" }}>Status</div>}
          open={open}
          centered
          onOk={onUpdate}
          okButtonProps={{
            disabled: isPending,
            loading: isPending,
          }}
          onCancel={() => {
            setStatus(undefined);
            setOpen(false);
          }}
          closable
          style={{ color: "black" }}
        >
          <Select
            value={status}
            placeholder="Update status"
            style={{ width: "100%" }}
            onChange={(val: any) => setStatus(val)}
            options={["ACTIVE", "INACTIVE"].map((_status: string) => {
              return { label: _status, value: _status };
            })}
          />
        </Modal>
        {/* <TableFilter filters={filters} setSelectedFilterIndex={setSelectedFilterIndex} /> */}
        <Table
          style={{ cursor: "pointer" }}
          onRow={(record, index) => {
            return {
              onClick: (event) => {
                if (record?.id) {
                  navigate(`/organizations/${record.id}`);
                }
              },
            };
          }}
          dataSource={tableData}
          columns={columns}
          loading={isLoading}
        />
      </div>
      <div className="block md:hidden text-white sticky top-0">
        <Input
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
          style={{ backgroundColor: "#08453E", color: "white", height: 40 }}
        />
      </div>
      <div className="w-full flex flex-col justify-between md:hidden">
        {tableData &&
          tableData.map((item: any) => {
            return (
              <Collapse
                key={item.id}
                accordion
                className="mb-1"
                items={[
                  {
                    key: item.id,
                    label: item.name,
                    children: (
                      <div
                        className="flex-col justify-between"
                        onClick={() => navigate(`/organizations/${item.id}`)}
                      >
                        <div className="py-1">
                          Type:{" "}
                          {item.type === "MICRO-FINANCE" ? "BANK" : item.type}
                        </div>
                        <div className="py-1">Email: {item.email}</div>

                        <div className="py-1">Phone: {item.phoneNo}</div>
                        {/* <div className="py-1">Address: {item.address}</div> */}
                        <div className="py-1">
                          NoDouments: {item.noDocument}
                        </div>
                        <div style={{ color: getStatusColor(item.status) }}>
                          {item.status}
                        </div>
                        {/* <Button
                          className="w-40"
                          onClick={() => {
                            navigate(`/organization/${organizations?.data.id}`);
                          }}
                        /> */}
                      </div>
                    ),
                  },
                ]}
              />
            );
          })}
      </div>
    </>
  );
};

export default OrganizationTable;
